import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { endUserAPI, ENDUSER_TOKEN_KEY, ENDUSER_DATA_KEY } from "@/lib/api";

// Contexto de identidad del usuario final (paciente/atleta) para la PWA /app.
// Independiente del AuthProvider del staff: distinto token (enduser_token) y
// distinto endpoint (/enduser/me).
const EndUserContext = createContext(null);

export function EndUserAuthProvider({ children }) {
  const [endUser, setEndUser] = useState(null);
  const [relationships, setRelationships] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem(ENDUSER_TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await endUserAPI.me();
      setEndUser(res.data.end_user);
      setRelationships(res.data.relationships || []);
    } catch {
      localStorage.removeItem(ENDUSER_TOKEN_KEY);
      localStorage.removeItem(ENDUSER_DATA_KEY);
      setEndUser(null);
      setRelationships([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const persist = (data) => {
    localStorage.setItem(ENDUSER_TOKEN_KEY, data.token);
    localStorage.setItem(ENDUSER_DATA_KEY, JSON.stringify(data.end_user));
    setEndUser(data.end_user);
  };

  const login = async (email, password) => {
    const res = await endUserAPI.login({ email, password });
    persist(res.data);
    await loadMe();
    return res.data;
  };

  const claim = async ({ email, code, name, password }) => {
    const res = await endUserAPI.claim({ email, code, name, password });
    persist(res.data);
    await loadMe();
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem(ENDUSER_TOKEN_KEY);
    localStorage.removeItem(ENDUSER_DATA_KEY);
    setEndUser(null);
    setRelationships([]);
  };

  return (
    <EndUserContext.Provider
      value={{ endUser, relationships, loading, login, claim, logout, loadMe }}
    >
      {children}
    </EndUserContext.Provider>
  );
}

export function useEndUser() {
  const ctx = useContext(EndUserContext);
  if (!ctx) throw new Error("useEndUser must be used within EndUserAuthProvider");
  return ctx;
}
